import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { getCurrentServerTime } from "libs/context/serverTimeBridge";

const padTwo = (value = 0) => String(Math.max(0, Number(value) || 0)).padStart(2, "0");

export const resolveRtrAcknowledgeCreatedDate = (row = {}) =>
  row?.createdDate ??
  row?.createAt ??
  row?.createdOn ??
  row?.created_at ??
  "";

const ZERO_ELAPSED_PARTS = {
  hours: "00",
  minutes: "00",
};

const normalizeRtrAcknowledgeDecisionValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const isRtrAcknowledgeAcknowledged = (row = {}) => {
  const decisionDisplay = normalizeRtrAcknowledgeDecisionValue(
    row?.decisionDisplay ?? row?.decision_display
  );
  const acknowledgeType = normalizeRtrAcknowledgeDecisionValue(
    row?.acknowledgeType ?? row?.acknowledge_type
  );

  return (
    (decisionDisplay === "yes" || decisionDisplay === "no") &&
    (acknowledgeType === "yes" || acknowledgeType === "no")
  );
};

export const getRtrAcknowledgeElapsedParts = (createdDate, nowMs = Date.now()) => {
  const start = dayjs(createdDate);
  if (!start.isValid()) {
    return { hours: "00", minutes: "00" };
  }

  const totalSeconds = Math.max(0, Math.floor((nowMs - start.valueOf()) / 1000));
  return {
    hours: padTwo(Math.floor(totalSeconds / 3600)),
    minutes: padTwo(Math.floor((totalSeconds % 3600) / 60)),
  };
};

export const formatRtrAcknowledgeElapsedTime = (createdDate, nowMs = Date.now()) => {
  const parts = getRtrAcknowledgeElapsedParts(createdDate, nowMs);
  return `${parts.hours}:${parts.minutes}`;
};

const TIMER_SEGMENTS = [
  { key: "hours", label: "Hours" },
  { key: "minutes", label: "Minutes" },
];

const RtrAcknowledgeElapsedTimer = ({ createdDate = "", frozen = false }) => {
  const [nowMs, setNowMs] = useState(() => getCurrentServerTime().getTime());

  useEffect(() => {
    if (frozen) return undefined;

    const timerId = setInterval(() => {
      setNowMs(getCurrentServerTime().getTime());
    }, 1000);
    return () => clearInterval(timerId);
  }, [frozen]);

  const elapsedParts = useMemo(() => {
    if (frozen) return ZERO_ELAPSED_PARTS;
    return getRtrAcknowledgeElapsedParts(createdDate, nowMs);
  }, [createdDate, nowMs, frozen]);

  return (
    <div className="rtr-acknowledge-elapsed-timer" role="timer" aria-live="polite">
      {TIMER_SEGMENTS.map((segment, index) => (
        <React.Fragment key={segment.key}>
          {index > 0 ? (
            <span className="rtr-acknowledge-elapsed-timer__colon" aria-hidden="true">
              :
            </span>
          ) : null}
          <div className="rtr-acknowledge-elapsed-timer__segment">
            <div className="rtr-acknowledge-elapsed-timer__digits">
              {elapsedParts[segment.key].split("").map((digit, digitIndex) => (
                <span
                  key={`${segment.key}-${digitIndex}`}
                  className="rtr-acknowledge-elapsed-timer__digit"
                >
                  {digit}
                </span>
              ))}
            </div>
            <span className="rtr-acknowledge-elapsed-timer__label">{segment.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

RtrAcknowledgeElapsedTimer.propTypes = {
  createdDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  frozen: PropTypes.bool,
};

export default RtrAcknowledgeElapsedTimer;
